import { getCurrentUser, getToken } from './authService';
import api from './axiosInstance';

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

const IMPERSONATION_BACKUP_TOKEN_KEY = 'impersonation_admin_token';
const IMPERSONATION_BACKUP_USER_KEY = 'impersonation_admin_user';

/**
 * @param {string} email
 * @returns {Promise<LoginResponse>}
 */

export async function impersonateByEmail(email) {
  if (!email || typeof email !== 'string') {
    throw new Error('Email is required.');
  }

  const adminToken = getToken();
  const adminUser = getCurrentUser();

  if (!adminToken || !adminUser) {
    throw new Error('No active admin session.');
  }

  try {
    localStorage.setItem(IMPERSONATION_BACKUP_TOKEN_KEY, adminToken);
    localStorage.setItem(
      IMPERSONATION_BACKUP_USER_KEY,
      JSON.stringify(adminUser)
    );

    const response = await api.post(
      'Auth/impersonate',
      { email: email.trim() },
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      }
    );

    if (!response.data?.token) {
      throw new Error('No token returned from impersonation.');
    }

    localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
    localStorage.setItem(
      AUTH_USER_KEY,
      JSON.stringify({
        userId: response.data.userId,
        name: response.data.name,
        email: response.data.email,
        role: response.data.role,
        expiresAt: response.data.expiresAt,
      })
    );

    return response.data;
  } catch (error) {
    localStorage.removeItem(IMPERSONATION_BACKUP_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_BACKUP_USER_KEY);

    const message =
      error.response?.data?.message ||
      error.message ||
      'Error impersonating user';

    throw new Error(message);
  }
}

export function stopImpersonation() {
  const adminToken = localStorage.getItem(IMPERSONATION_BACKUP_TOKEN_KEY);
  const adminUser = localStorage.getItem(IMPERSONATION_BACKUP_USER_KEY);

  if (!adminToken || !adminUser) return;

  localStorage.setItem(AUTH_TOKEN_KEY, adminToken);
  localStorage.setItem(AUTH_USER_KEY, adminUser);

  localStorage.removeItem(IMPERSONATION_BACKUP_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATION_BACKUP_USER_KEY);
}

export function isImpersonating() {
  return (
    !!localStorage.getItem(IMPERSONATION_BACKUP_TOKEN_KEY) &&
    !!localStorage.getItem(IMPERSONATION_BACKUP_USER_KEY)
  );
}

const impersonationService = {
  impersonateByEmail,
  stopImpersonation,
  isImpersonating,
};

export default impersonationService;
